import * as React from 'react';
import * as styled from './styled';
import {useFormikContext} from 'formik';
import SlideDown from 'react-slidedown';
import Switch from 'components/Switch';
import {SettingsGroupProps, MapContent} from './types';

/**
 * The component render group of settings.
 * @param {SettingsGroupProps} props
 * @return {React.ReactElement<SettingsGroupProps>}
 * @constructor
 */
export default function SettingsGroup<T extends MapContent>(
    props: SettingsGroupProps<T>,
): React.ReactElement<SettingsGroupProps<T>> {
  const {
    values, setFieldValue,
  } = useFormikContext<T>();

  /**
   * Is the settings group active.
   * @return {boolean | undefined}
   */
  function isActive(): boolean | undefined {
    return props.name ? (
      values.settings[props.name]?.active as boolean
    ) : undefined;
  }

  /**
   * Render toggle.
   * @return {JSX.Element | undefined}
   */
  function renderToggle(): JSX.Element | undefined {
    /**
     * The change event handler.
     * @return {void}t
     */
    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
      setFieldValue(`settings.${props.name}.active`, event.target.checked);
    }

    return props.name ? (
      <Switch
        checked={isActive()}
        onChange={handleChange}
      />
    ) : undefined;
  }

  /**
   * Render title.
   * @return {JSX.Element | undefined}
   */
  function renderTitle(): JSX.Element | undefined {
    return props.title ? (
      <styled.Title>
        {props.title}
      </styled.Title>
    ) : undefined;
  }

  /**
   * Render description
   * @return {JSX.Element | undefined}
   */
  function renderDescription(): JSX.Element | undefined {
    return props.description ? (
      <styled.Description>
        {props.description}
      </styled.Description>
    ) : undefined;
  }

  /**
   * Render content.
   * @return {JSX.Element | undefined}
   */
  function renderContent(): JSX.Element {
    const render: JSX.Element = (
      <styled.Content>
        {renderDescription()}
        {props.children}
      </styled.Content>
    );

    return (
      <React.Fragment>
        {props.name ? (
          <SlideDown
            closed={!isActive()}
            transitionOnAppear={false}>
            {render}
          </SlideDown>
        ) : render}
      </React.Fragment>
    );
  }

  return (
    <styled.Wrapper>
      {(renderToggle() !== undefined || renderTitle() !== undefined) && (
        <styled.Header>
          {renderToggle()}
          {renderTitle()}
        </styled.Header>
      )}
      {renderContent()}
    </styled.Wrapper>
  );
}
